import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { GoogleMap, Marker, InfoWindow, useLoadScript } from '@react-google-maps/api';
import { useTranslation } from 'react-i18next';

const StoresMap = React.memo(({ stores = [], onStoreSelect, selectedStore, selectedFilter }) => {
  const { t } = useTranslation('store');
  const [isMapVisible, setIsMapVisible] = useState(false);
  const [autoShowStore, setAutoShowStore] = useState(null);
  const [currentZoom, setCurrentZoom] = useState(10);
  const mapRef = useRef(null);
  const containerRef = useRef(null);

  // Always call useLoadScript hook - this fixes the conditional hook issue
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: "AIzaSyCO0kKndUNlmQi3B5mxy4dblg_8WYcuKuk",
    language: 'sr',
    region: 'RS'
  });

  // Intersection Observer for lazy loading
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isMapVisible) {
          setIsMapVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, [isMapVisible]);

  // Memoize map center calculation
  const mapCenter = useMemo(() => {
    if (selectedFilter === 'beograd') {
      return { lat: 44.8125, lng: 20.4294 }; // Beograd
    } else if (selectedFilter === 'ostali' || selectedFilter === 'svi') {
      return { lat: 44.5, lng: 20.5 }; // Sredina Srbije
    }
    return { lat: 44.8125, lng: 20.4294 }; // Default Beograd
  }, [selectedFilter]);

  // Memoize zoom level calculation
  const zoomLevel = useMemo(() => {
    if (selectedFilter === 'beograd') {
      return 10; // Zum za Beograd
    } else if (selectedFilter === 'ostali' || selectedFilter === 'svi') {
      return 7; // Zum za celu Srbiju
    }
    return 10; // Default
  }, [selectedFilter]);

  // Auto-show effect for single store
  useEffect(() => {
    const timer = setTimeout(() => {
      if (currentZoom >= 16 && stores.length === 1 && mapRef.current) {
        setAutoShowStore(stores[0]);
      } else {
        setAutoShowStore(null);
      }
    }, 200);

    return () => clearTimeout(timer);
  }, [currentZoom, stores]);

  const mapContainerStyle = useMemo(() => ({
    width: '100%',
    height: '450px',
    borderRadius: '1rem',
    pointerEvents: 'auto'
  }), []);

  const mapOptions = useMemo(() => ({
    disableDefaultUI: false,
    clickableIcons: true,
    scrollwheel: false,
    gestureHandling: 'cooperative',
    zoomControl: true,
    mapTypeControl: false,
    scaleControl: true,
    streetViewControl: true,
    rotateControl: false,
    fullscreenControl: true,
    styles: [
      {
        featureType: "poi",
        elementType: "labels",
        stylers: [{ visibility: "off" }]
      },
      {
        featureType: "transit",
        elementType: "labels",
        stylers: [{ visibility: "off" }]
      }
    ]
  }), []);

  const onMapClick = useCallback(() => {
    setAutoShowStore(null);
  }, []);

  const onMarkerClick = useCallback((store) => {
    if (onStoreSelect) {
      onStoreSelect(store);
    }
  }, [onStoreSelect]);

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  const onZoomChanged = useCallback(() => {
    if (mapRef.current) {
      try {
        const newZoom = mapRef.current.getZoom();
        if (newZoom !== currentZoom) {
          setCurrentZoom(newZoom);
        }
      } catch (error) {
        console.warn('Error getting zoom level:', error);
      }
    }
  }, [currentZoom]);

  // Generate static map URL for placeholder
  const staticMapUrl = useMemo(() => {
    const markers = stores
      .filter(store => store.coordinates)
      .map(store => {
        const color = store.region === 'beograd' ? 'red' : 'blue';
        return `markers=color:${color}|size:small|${store.coordinates.lat},${store.coordinates.lng}`;
      })
      .join('&');

    return `https://maps.googleapis.com/maps/api/staticmap?center=${mapCenter.lat},${mapCenter.lng}&zoom=${zoomLevel}&size=800x450&maptype=roadmap&${markers}&key=AIzaSyCO0kKndUNlmQi3B5mxy4dblg_8WYcuKuk&style=feature:poi|element:labels|visibility:off&style=feature:transit|element:labels|visibility:off`;
  }, [stores, mapCenter, zoomLevel]);

  // Memoize marker icons to prevent recreation on every render
  const getMarkerIcon = useCallback((store, isSelected) => ({
    path: window.google?.maps?.SymbolPath?.CIRCLE || 0,
    scale: isSelected ? 12 : 10,
    fillColor: store.region === 'beograd' ? '#8b7355' : '#a0845c',
    fillOpacity: isSelected ? 1 : 0.8,
    strokeColor: isSelected ? '#2d2520' : '#ffffff',
    strokeWeight: isSelected ? 3 : 2
  }), []);

  // Memoize markers to prevent unnecessary re-renders
  const markers = useMemo(() => {
    if (!isLoaded) return [];
    
    return stores.map((store) => {
      if (!store.coordinates) return null;
      
      const isSelected = selectedStore?.id === store.id;
      
      return (
        <Marker
          key={store.id}
          position={store.coordinates}
          onClick={() => onMarkerClick(store)}
          icon={getMarkerIcon(store, isSelected)}
          title={store.name}
          zIndex={10}
        />
      );
    }).filter(Boolean);
  }, [stores, selectedStore, onMarkerClick, getMarkerIcon, isLoaded]);

  if (loadError) {
    return (
      <div className="flex items-center justify-center h-96 bg-muted/10 rounded-xl">
        <p className="text-red-500">{t('map.loadError', 'Greška pri učitavanju mape')}</p>
      </div>
    );
  }

  // Show static map placeholder until Google Maps loads
  if (!isMapVisible || !isLoaded) {
    return (
      <div ref={containerRef} className="relative">
        <div className="relative w-full h-[450px] rounded-xl overflow-hidden bg-gray-100" style={{ contain: 'paint' }}>
          <img
            src={staticMapUrl}
            alt={t('map.alt', 'Mapa prodavnica')}
            className="w-full h-full object-cover"
            loading="lazy"
          />
          
          {/* Loading overlay */}
          {isMapVisible && (
            <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
              <div className="bg-white rounded-lg p-4 shadow-lg">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                <p className="text-sm text-gray-600">{t('map.loadingInteractive', 'Učitavanje interaktivne mape...')}</p>
              </div>
            </div>
          )}
        </div>
        
        {/* Legend */}
        <div className="mt-4 flex items-center justify-center space-x-6 text-sm text-gray-600">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-red-500 rounded-full"></div>
            <span>{t('map.belgrade', 'Beograd')}</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
            <span>{t('map.otherCities', 'Ostali gradovi')}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={mapCenter}
        zoom={zoomLevel}
        options={mapOptions}
        onClick={onMapClick}
        onLoad={onMapLoad}
        onZoomChanged={onZoomChanged}
      >
        {markers}

        {/* Auto-show info window when zoomed close */}
        {autoShowStore && autoShowStore.coordinates && (
          <InfoWindow
            position={autoShowStore.coordinates}
            options={{
              pixelOffset: window.google?.maps ? new window.google.maps.Size(0, -40) : undefined
            }}
          >
            <div className="p-3 min-w-64 max-w-sm">
              <div className="mb-2">
                <h3 className="font-semibold text-gray-900 text-sm mb-1">{autoShowStore.name}</h3>
                <p className="text-xs text-gray-600">{autoShowStore.city}</p>
              </div>
              
              <div className="space-y-1 mb-3 text-xs">
                <p className="text-gray-600">{autoShowStore.address}</p>
                <p className="text-green-600 font-medium">{autoShowStore.phone}</p>
                <p className="text-gray-600">{autoShowStore.hours}</p>
              </div>
              
              <button
                onClick={() => {
                  if (onStoreSelect) {
                    onStoreSelect(autoShowStore);
                  }
                }}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg text-xs font-medium transition-colors duration-200"
              >
                {t('map.moreInfo', 'Više informacija')}
              </button>
            </div>
          </InfoWindow>
        )}
      </GoogleMap>
    </div>
  );
});

StoresMap.displayName = 'StoresMap';

export default StoresMap;